import React, { useState } from 'react';
import './ResultsTable.css';
import { highlightMatches, shouldCollapse } from '../utils/helpers';
import Pagination from './Pagination';

function ResultsTable({ results, query, total, cursor, limit, visibleColumns, searchType, onNextPage, onPrevPage }) {
  const isB2BSaaS = searchType === 'b2bsaas';
  
  return (
    <div className="results-container">
      <div className="results-header">
        <div className="results-count">
          Found <strong>{total}</strong> result(s). Showing{' '}
          <strong>{results.length}</strong> result(s)
        </div>
      </div>

      <div className="table-wrapper">
        <table className="results-table">
          <thead>
            <tr>
              {isB2BSaaS ? (
                <>
                  <th>Company Keyword</th>
                  <th>URL</th>
                  <th>SaaS Platform</th>
                  <th>Detected At</th>
                  <th>Status Code</th>
                </>
              ) : (
                <>
                  <th>IP Address</th>
                  <th>Provider</th>
                  <th>Subject CN</th>
                  {visibleColumns.fingerprint && <th>Fingerprint SHA256</th>}
                  <th>Issuer CN</th>
                  <th>Issuer DN</th>
                  <th>Issuer OU</th>
                  {visibleColumns.validFrom && <th>Valid From</th>}
                  {visibleColumns.validTo && <th>Valid To</th>}
                  <th>Collected At</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {results.map((item, index) => (
              isB2BSaaS ? (
                <B2BSaaSRow
                  key={`${item.url}-${item.company_keyword}-${index}`}
                  item={item}
                  query={query}
                />
              ) : (
                <TableRow
                  key={`${item.ip_address}-${index}`}
                  cert={item}
                  query={query}
                  visibleColumns={visibleColumns}
                />
              )
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        total={total}
        hasNext={!!cursor}
        hasPrev={false} // Курсорная пагинация только вперед
        onNextPage={onNextPage}
        onPrevPage={onPrevPage}
      />
    </div>
  );
}

function TableRow({ cert, query, visibleColumns }) {
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = shouldCollapse(cert);
  const searchQuery = query ? query.toLowerCase() : '';

  const renderSubjectCN = () => {
    const subjectCn = cert.subject_cn || '';
    if (subjectCn.includes(',')) {
      const cnParts = subjectCn.split(',').map((s) => s.trim()).filter((s) => s);
      return (
        <div className={`cn-list ${expanded ? 'expanded' : 'collapsed'}`}>
          {cnParts.map((cnPart, idx) => (
            <div key={idx} className="cn-item">
              <span className="cn-marker">•</span>
              <span
                className="cn-value"
                dangerouslySetInnerHTML={{ __html: highlightMatches(cnPart, searchQuery) }}
              />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="cn-single">
        <span className="cn-marker">•</span>
        <span
          className="cn-value"
          dangerouslySetInnerHTML={{ __html: highlightMatches(subjectCn, searchQuery) }}
        />
      </div>
    );
  };

  return (
    <tr
      className={`table-row ${needsCollapse ? 'collapsible' : ''} ${expanded ? 'expanded' : ''}`}
      onClick={() => needsCollapse && setExpanded(!expanded)}
    >
      <td
        dangerouslySetInnerHTML={{ __html: highlightMatches(cert.ip_address, searchQuery) }}
      />
      <td>
        <span className={`provider-badge provider-${(cert.provider || '').toLowerCase()}`}>
          {cert.provider || '-'}
        </span>
      </td>
      <td className="subject-cn-cell">
        {needsCollapse && (
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        )}
        {renderSubjectCN()}
      </td>
      {visibleColumns.fingerprint && (
        <td className="fingerprint-cell">
          {cert.fingerprint_sha256 ? (
            <span
              dangerouslySetInnerHTML={{
                __html: highlightMatches(cert.fingerprint_sha256, searchQuery),
              }}
            />
          ) : (
            '-'
          )}
        </td>
      )}
      <td
        dangerouslySetInnerHTML={{ __html: highlightMatches(cert.issuer || '-', searchQuery) }}
      />
      <td
        className="issuer-dn-cell"
        title={cert.issuer_dn || ''}
        dangerouslySetInnerHTML={{
          __html: highlightMatches(cert.issuer_dn || '-', searchQuery),
        }}
      />
      <td
        dangerouslySetInnerHTML={{ __html: highlightMatches(cert.issuer_ou || '-', searchQuery) }}
      />
      {visibleColumns.validFrom && (
        <td>
          {cert.valid_from
            ? new Date(cert.valid_from).toLocaleDateString()
            : '-'}
        </td>
      )}
      {visibleColumns.validTo && (
        <td>
          {cert.valid_to ? new Date(cert.valid_to).toLocaleDateString() : '-'}
        </td>
      )}
      <td>
        {cert.collected_at
          ? new Date(cert.collected_at).toLocaleString()
          : '-'}
      </td>
    </tr>
  );
}

function B2BSaaSRow({ item, query }) {
  const searchQuery = query ? query.toLowerCase() : '';
  
  return (
    <tr>
      <td
        dangerouslySetInnerHTML={{ __html: highlightMatches(item.company_keyword || '-', searchQuery) }}
      />
      <td>
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          {item.url || '-'}
        </a>
      </td>
      <td>
        <span className={`saas-platform-badge platform-${(item.saas_platform || '').toLowerCase()}`}>
          {item.saas_platform || '-'}
        </span>
      </td>
      <td>
        {item.detected_at
          ? new Date(item.detected_at).toLocaleString()
          : '-'}
      </td>
      <td>{item.status_code || '-'}</td>
    </tr>
  );
}

export default ResultsTable;

